import { istToday } from "@/lib/stayflowApi";
import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { format } from "date-fns";
import { Search, X } from "lucide-react";
import { toast } from "sonner";
import { Stepper } from "../SearchWidget";
import { WhereSuggestions } from "./WhereSuggestions";

const iso = (d) => format(d, "yyyy-MM-dd");


const spring = { type: "spring", stiffness: 420, damping: 36 };

const CollapsedPill = ({ label, value, onClick, testid }) => (
  <motion.button layout transition={spring} onClick={onClick} data-testid={testid}
    className="w-full flex items-center justify-between rounded-2xl bg-white shadow-md border border-black/5 px-5 py-4 active:scale-[0.98] transition-transform">
    <span className="text-sm font-semibold text-ink-soft">{label}</span>
    <span className="text-sm font-bold text-ink truncate max-w-[60%]">{value}</span>
  </motion.button>
);

const Card = ({ children, testid }) => (
  <motion.div layout transition={spring} initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }}
    className="bg-white rounded-3xl p-5 shadow-xl border border-black/5" data-testid={testid}>
    {children}
  </motion.div>
);

// 3-step progressive (Where → When → Who) full-screen search overlay.
export const SearchOverlay = ({ open, onClose, initial = {}, onSearch, stays = [], onPickStay }) => {
  const [step, setStep] = useState("where");
  const [where, setWhere] = useState("");
  const [nearby, setNearby] = useState(null);
  const [picked, setPicked] = useState(null);
  const [locating, setLocating] = useState(false);
  const [range, setRange] = useState(undefined);
  const [flex, setFlex] = useState(0);
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [infants, setInfants] = useState(0);
  const [pets, setPets] = useState(0);
  const [rooms, setRooms] = useState(1);

  useEffect(() => {
    if (!open) return;
    setStep("where");
    setWhere(initial.search || "");
    setNearby(initial.near || null);
    setPicked(initial.picked || null);
    setRange(initial.dateRange);
    setFlex(initial.flex || 0);
    setAdults(initial.adults ?? 2);
    setChildren(initial.children ?? 0);
    setInfants(initial.infants ?? 0);
    setPets(initial.pets ?? 0);
    setRooms(initial.rooms ?? 1);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const today = useMemo(() => istToday(), []);
  const guests = adults + children;
  const whereLabel = nearby ? "Nearby" : where.trim() || "Add destination";
  const whenLabel = range?.from && range?.to
    ? `${format(range.from, "dd MMM")} – ${format(range.to, "dd MMM")}${flex ? ` (±${flex}d)` : ""}`
    : "Add dates";
  const whoLabel = [
    `${guests} guest${guests !== 1 ? "s" : ""}`,
    infants > 0 ? `${infants} infant${infants !== 1 ? "s" : ""}` : null,
    pets > 0 ? `${pets} pet${pets !== 1 ? "s" : ""}` : null,
  ].filter(Boolean).join(", ");

  const pickPlace = (p) => { setWhere(p.main); setPicked(p); setNearby(null); setStep("when"); };

  const pickNearby = () => {
    if (!("geolocation" in navigator)) { toast.error("Location isn't supported on this device."); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setLocating(false); setNearby({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setPicked(null); setWhere(""); setStep("when"); },
      (err) => { setLocating(false); toast.error(err.code === 1 ? "Please allow location access to use Nearby." : "Couldn't get your location. Try again."); },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  const clearAll = () => { setWhere(""); setNearby(null); setPicked(null); setRange(undefined); setFlex(0); setAdults(2); setChildren(0); setInfants(0); setPets(0); setRooms(1); setStep("where"); };

  const doSearch = () => {
    const p = new URLSearchParams();
    if (nearby) p.set("near", `${nearby.lat.toFixed(5)},${nearby.lng.toFixed(5)}`);
    else if (picked?.place_id) { p.set("place", picked.main); p.set("placeId", picked.place_id); }
    else if (where.trim()) p.set("location", where.trim());
    if (range?.from && range?.to) {
      p.set("checkIn", iso(range.from)); p.set("checkOut", iso(range.to));
      if (flex) p.set("flex", String(flex));
    }
    p.set("adults", String(adults)); p.set("children", String(children)); p.set("rooms", String(rooms));
    if (infants) p.set("infants", String(infants));
    if (pets) p.set("pets", String(pets));
    // Close first, then apply params on the next tick so the parent re-render
    // doesn't interrupt AnimatePresence's exit (previously left a ghost overlay).
    onClose();
    setTimeout(() => onSearch(p), 30);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div key="search-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, pointerEvents: "none" }} transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[60] flex flex-col bg-paper-muted/80 backdrop-blur-md" data-testid="search-overlay">
          <div className="flex items-center justify-between px-4 pt-4">
            <p className="font-heading font-extrabold text-lg text-ink pl-1">Find a stay</p>
            <button onClick={onClose} aria-label="Close search" data-testid="overlay-close"
              className="w-10 h-10 rounded-full bg-white shadow-md border border-black/5 flex items-center justify-center active:scale-95 transition-transform">
              <X className="w-4 h-4 text-ink" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 pt-3 pb-4 space-y-3" data-lenis-prevent>
            {/* WHERE */}
            {step === "where" ? (
              <Card testid="step-where">
                <p className="text-2xl font-heading font-extrabold text-ink mb-4">Where?</p>
                <div className="flex items-center gap-2.5 rounded-2xl border border-black/15 px-4 py-3 focus-within:border-ink transition-colors">
                  <Search className="w-4 h-4 text-ink-faint shrink-0" />
                  <input value={where} onChange={(e) => { setWhere(e.target.value); setNearby(null); setPicked(null); }} placeholder="Search destinations"
                    autoComplete="off" data-testid="overlay-where-input"
                    className="w-full bg-transparent outline-none text-ink font-medium placeholder:text-ink-faint" />
                </div>
                <div className="mt-4 -mx-5">
                  <WhereSuggestions
                    query={where} stays={stays} locating={locating} big
                    onPickPlace={pickPlace}
                    onPickStay={(st) => { onClose(); setTimeout(() => onPickStay?.(st), 30); }}
                    onNearMe={pickNearby}
                  />
                </div>
              </Card>
            ) : (
              <CollapsedPill label="Where" value={whereLabel} onClick={() => setStep("where")} testid="pill-where" />
            )}

            {/* WHEN */}
            {step === "when" ? (
              <Card testid="step-when">
                <p className="text-2xl font-heading font-extrabold text-ink mb-1">When?</p>
                <div className="flex justify-center rdp-wrap" style={{ "--rdp-accent-color": "#FF1A1A", "--rdp-background-color": "#FFE9E7", "--rdp-cell-size": "42px" }}>
                  <DayPicker mode="range" selected={range} onSelect={setRange} numberOfMonths={1} fromDate={today} defaultMonth={range?.from || today} />
                </div>
                <div className="mt-2 flex gap-2 overflow-x-auto no-scrollbar" data-testid="flex-chips">
                  {[0, 1, 2, 3].map((d) => (
                    <button key={d} onClick={() => setFlex(d)} data-testid={`flex-${d}`} aria-pressed={flex === d}
                      className={`shrink-0 rounded-full border px-3.5 h-9 text-[13px] font-semibold transition-colors active:scale-95 ${
                        flex === d ? "border-ink bg-ink text-white" : "border-black/15 bg-white text-ink-soft"
                      }`}>
                      {d === 0 ? "Exact dates" : `± ${d} day${d > 1 ? "s" : ""}`}
                    </button>
                  ))}
                </div>
              </Card>
            ) : (
              <CollapsedPill label="When" value={whenLabel} onClick={() => setStep("when")} testid="pill-when" />
            )}

            {/* WHO */}
            {step === "who" ? (
              <Card testid="step-who">
                <p className="text-2xl font-heading font-extrabold text-ink mb-1">Who?</p>
                <div className="divide-y divide-black/5">
                  <Stepper label="Adults" sub="Ages 13 or above" value={adults} setValue={setAdults} min={1} max={30} testid="who-adults" />
                  <Stepper label="Children" sub="Ages 2–12" value={children} setValue={setChildren} min={0} max={20} testid="who-children" />
                  <Stepper label="Infants" sub="Under 2 — don't count toward capacity" value={infants} setValue={setInfants} min={0} max={10} testid="who-infants" />
                  <Stepper label="Pets" sub="Bringing a service animal?" value={pets} setValue={setPets} min={0} max={5} testid="who-pets" />
                  <Stepper label="Rooms" value={rooms} setValue={setRooms} min={1} max={20} testid="who-rooms" />
                </div>
              </Card>
            ) : (
              <CollapsedPill label="Who" value={whoLabel} onClick={() => setStep("who")} testid="pill-who" />
            )}
          </div>

          <div className="border-t border-black/5 bg-white/95 backdrop-blur px-5 py-3 pb-[max(env(safe-area-inset-bottom),12px)] flex items-center justify-between gap-3">
            {step === "when" ? (
              <>
                <button onClick={() => { setRange(undefined); setFlex(0); }} data-testid="overlay-reset-dates"
                  className="font-semibold text-ink underline underline-offset-4 px-2 py-2 active:scale-95 transition-transform">Reset</button>
                <button onClick={() => setStep("who")} data-testid="overlay-next"
                  className="bg-ink text-white font-heading font-bold px-7 h-12 rounded-2xl active:scale-95 transition-transform">Next</button>
              </>
            ) : (
              <>
                <button onClick={clearAll} data-testid="overlay-clear-all"
                  className="font-semibold text-ink underline underline-offset-4 px-2 py-2 active:scale-95 transition-transform">Clear all</button>
                <button onClick={doSearch} data-testid="overlay-search"
                  className="inline-flex items-center gap-2 rounded-2xl bg-brand text-white font-heading font-bold px-7 h-12 shadow-[0_10px_28px_rgba(255,26,26,0.4)] active:scale-[0.97] transition-transform">
                  <Search className="w-[18px] h-[18px]" /> Search
                </button>
              </>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SearchOverlay;
